/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

// Agent keyboard shortcuts use a platform-aware modifier. macOS keeps its
// browser/OS shortcuts on Cmd, leaving Ctrl free for the app. Windows/Linux
// keep those same shortcuts on Ctrl (Ctrl+W closes the tab, Ctrl+N opens a
// window, Ctrl+1..9 switch tabs — none of which a page can intercept), so we
// mirror to Alt, the closest app-free modifier there.

export const isMacPlatform = () =>
  typeof window !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(window.navigator.platform);

type ModifierEvent = Pick<KeyboardEvent, 'ctrlKey' | 'metaKey' | 'altKey'>;

// True when the platform's agent modifier (and only it) is held.
export const hasAgentShortcutModifier = (event: ModifierEvent) =>
  isMacPlatform()
    ? event.ctrlKey && !event.metaKey && !event.altKey
    : event.altKey && !event.ctrlKey && !event.metaKey;

// The `KeyboardEvent.key` value for the modifier, for keydown/keyup held tracking.
export const agentShortcutModifierKey = () => (isMacPlatform() ? 'Control' : 'Alt');

// Bare label for the modifier keycap in hints: "⌃" on Mac, "Alt" elsewhere.
export const agentShortcutModifierLabel = () => (isMacPlatform() ? '⌃' : 'Alt');

// A full "modifier + key" hint label, e.g. "⌃W" on Mac or "Alt+W" on Windows.
export const formatAgentShortcut = (key: string) => (isMacPlatform() ? `⌃${key}` : `Alt+${key}`);

// The same shortcut in `aria-keyshortcuts` syntax ("Control+A" / "Alt+A"); glyphs aren't valid there.
export const formatAgentAriaShortcut = (key: string) => `${agentShortcutModifierKey()}+${key}`;
